import React, { useState, useEffect } from 'react';
import { PAGE, SettingsHeader } from './SettingsUI';
import { Save, Loader2, Pencil, Users } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';

function Toggle({ checked, onChange, disabled }) {
  return (
    <button type="button" onClick={() => !disabled && onChange(!checked)} disabled={disabled}
      className={clsx('relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none',
        checked ? 'bg-primary-600' : 'bg-slate-200', disabled && 'opacity-50 cursor-not-allowed')}>
      <span className={clsx('inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform',
        checked ? 'translate-x-6' : 'translate-x-1')} />
    </button>
  );
}

function Row({ label, hint, children }) {
  return (
    <div className="flex items-center justify-between gap-4 py-3 border-b border-slate-100 last:border-0">
      <div>
        <p className="text-sm font-medium text-slate-800">{label}</p>
        {hint && <p className="text-xs leading-relaxed text-slate-500 mt-0.5">{hint}</p>}
      </div>
      <div className="shrink-0 flex items-center gap-3">{children}</div>
    </div>
  );
}

const DEFAULTS = {
  view_activity_timeline: true,
  cancel_services: true,
  cancel_services_notice_hours: 48,
  view_staff_profile: true,
  view_notifications: true,
  view_client_profile: true,
  allow_viewing_shift_notes: true,
  allow_editing_shifts: false,
  allow_shift_requests: true,
  view_attachments: true,
};

export default function FamilyPortalSettings() {
  const [cfg, setCfg]         = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving]   = useState(false);

  useEffect(() => {
    api.get('/family-portal/settings')
      .then(r => { if (r.data && Object.keys(r.data).length) setCfg(prev => ({ ...prev, ...r.data })); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setCfg(c => ({ ...c, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/family-portal/settings', cfg);
      toast.success('Family portal settings saved');
      setEditing(false);
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className={PAGE}>
      <SettingsHeader title="Family portal" description="What family members can see and do in the client portal." />

      <div className="bg-white rounded-2xl border border-slate-200 p-5 max-w-2xl">
        <div className="flex items-center justify-between pb-3 border-b border-slate-100 mb-1">
          <h3 className="text-sm font-bold text-slate-900">Connect Permissions</h3>
          <div className="flex items-center gap-2">
            {editing ? (
              <>
                <button onClick={() => setEditing(false)}
                  className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors">
                  Cancel
                </button>
                <button onClick={save} disabled={saving}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-primary-50 text-primary-700 hover:bg-primary-100 transition-colors border border-primary-100">
                  {saving ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />} Save
                </button>
              </>
            ) : (
              <button onClick={() => setEditing(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors">
                <Pencil size={11} /> Edit
              </button>
            )}
          </div>
        </div>

        <Row label="View activity timeline" hint="Family can see a history of recent shifts.">
          <Toggle checked={cfg.view_activity_timeline} onChange={v => set('view_activity_timeline', v)} disabled={!editing} />
        </Row>

        <Row label="Cancel services" hint="Family members can request service cancellations.">
          {cfg.cancel_services && (
            <div className="flex items-center gap-1.5">
              <input type="number" min="0" max="168"
                value={cfg.cancel_services_notice_hours}
                onChange={e => set('cancel_services_notice_hours', parseInt(e.target.value) || 0)}
                disabled={!editing}
                className="w-16 border border-slate-200 rounded-lg px-2 py-1 text-sm text-center focus:outline-none focus:border-primary-400 disabled:bg-slate-50 disabled:text-slate-400" />
              <span className="text-xs text-slate-500 whitespace-nowrap">hours notice required</span>
            </div>
          )}
          <Toggle checked={cfg.cancel_services} onChange={v => set('cancel_services', v)} disabled={!editing} />
        </Row>

        <Row label="View staff profile" hint="Family can see profiles of assigned staff.">
          <Toggle checked={cfg.view_staff_profile} onChange={v => set('view_staff_profile', v)} disabled={!editing} />
        </Row>

        <Row label="View notifications" hint="Family receives notifications about service changes.">
          <Toggle checked={cfg.view_notifications} onChange={v => set('view_notifications', v)} disabled={!editing} />
        </Row>

        <Row label="View client profile" hint="Family can see the client's profile information.">
          <Toggle checked={cfg.view_client_profile} onChange={v => set('view_client_profile', v)} disabled={!editing} />
        </Row>

        <Row label="Allow viewing shift notes" hint="Family can read progress notes from shifts.">
          <Toggle checked={cfg.allow_viewing_shift_notes} onChange={v => set('allow_viewing_shift_notes', v)} disabled={!editing} />
        </Row>

        <Row label="Allow editing shifts" hint="Family members can edit upcoming shift details.">
          <Toggle checked={cfg.allow_editing_shifts} onChange={v => set('allow_editing_shifts', v)} disabled={!editing} />
        </Row>

        <Row label="Allow shift requests" hint="Family can submit requests to add or cancel shifts.">
          <Toggle checked={cfg.allow_shift_requests} onChange={v => set('allow_shift_requests', v)} disabled={!editing} />
        </Row>

        <Row label="View attachments" hint="Family can view documents shared on the client profile.">
          <Toggle checked={cfg.view_attachments} onChange={v => set('view_attachments', v)} disabled={!editing} />
        </Row>
      </div>
    </div>
  );
}
